import _ from 'lodash';
import React from 'react';
import {connect} from 'react-redux';
import classNames from 'classnames';

import Dropzone from 'components/Dropzone';
import VisibleIf from 'components/VisibleIf';

import {uploadFile, ACCEPTED_IMAGE_FILE_TYPES} from 'ducks/files';
import {delayedAction} from 'ducks/common';
import {requiresPermission, PERMISSIONS} from 'ducks/auth';
import {
  REFETCH_AVATARS_DELAY,
  verifyAgreementUserInternalAvatarUpload
} from 'ducks/agreementUsers';
import {openModal, MODALS as BASE_MODALS} from 'ducks/modals';
import {fetchMemberForDashboard, selectMember} from 'ducks/users';
import {signAgreementUserInternalAvatar} from 'sdk/agreementUsers';
import Button from 'components/Button';

import {headOption} from 'utils/common';
import {exportCsv, selectExporting} from 'ducks/files';
import {exportSingleMemberCsv as exportSingleMemberSdk} from 'sdk/users';

import './styles.css';

const MEMBERS_CSV = 'member';

interface PassedProps {
  locationRef: string;
  memberId: number;
}

interface MemberBoxState {
  avatarTabOpened: string;
}

const avatarTabs = [
  {
    ref: 'colabImage',
    label: 'Colab Image',
    memberObjectKey: 'internalAvatarCropped'
  },
  {ref: 'socialImage', label: 'Social Image', memberObjectKey: 'avatarCropped'}
];

const MemberAvatar = ({
  memberAvatar,
  uploadAvatar,
  openTakePhotoModal,
  avatarTabOpened
}) => (
  <div className="MemberBox__avatar">
    <div
      className="MemberBox__avatar__image"
      style={{backgroundImage: `url(${memberAvatar})`}}>
      {avatarTabOpened === 'colabImage' && (
        <React.Fragment>
          <div className="MemberBox__avatar__overlay">
            <Dropzone
              onDrop={uploadAvatar}
              multiple={false}
              acceptedFileTypes={ACCEPTED_IMAGE_FILE_TYPES}
              defaultComponent={
                <div className="MemberBox__avatar__button colab-centered colab-clickable">
                  Replace Image
                </div>
              }
            />
            <div
              className="MemberBox__avatar__button colab-centered colab-clickable"
              onClick={openTakePhotoModal}>
              Take Photo
            </div>
          </div>
        </React.Fragment>
      )}
    </div>
  </div>
);

class MemberBox extends React.Component<
  PassedProps & ReturnType<typeof mapStateToProps> & typeof mapDispatchToProps
> {
  state: MemberBoxState = {
    avatarTabOpened: 'colabImage'
  };

  changeActiveTab = (avatarTabRef: string) => {
    this.setState({avatarTabOpened: avatarTabRef});
  };

  isTabActive = (avatarTabRef: string) => {
    return this.state.avatarTabOpened === avatarTabRef;
  };

  getActiveTab = () => {
    return _.find(avatarTabs, tab => tab.ref === this.state.avatarTabOpened);
  };

  getMemberAvatar = () => {
    return _.get(
      this.props.member,
      _.get(this.getActiveTab(), 'memberObjectKey', '')
    );
  };

  openTakePhotoModal = () => {
    const {memberId, locationRef} = this.props;

    const extraActions = [
      () => fetchMemberForDashboard({memberId, locationRef})
    ];

    this.props.requiresPermission(
      {
        permission: PERMISSIONS.EDIT_MEMBER_INFORMATION,
        locationRef,
        disableClose: false
      },
      () => {
        this.props.openModal(BASE_MODALS.AGREEMENT_USER_TAKE_PHOTO, {
          locationRef,
          agreementUserId: memberId,
          extraActions
        });
      }
    );
  };

  uploadAvatar = files => {
    if (_.isEmpty(files)) {
      return;
    }

    const {memberId, locationRef} = this.props;

    const file = headOption(files);

    const signData = {
      filename: file.name,
      fileType: file.type,
      location: locationRef,
      agreementUserId: memberId
    };

    const finishUploadingAction = () =>
      delayedAction({
        delay: REFETCH_AVATARS_DELAY,
        action: () => fetchMemberForDashboard({memberId, locationRef})
      });

    const storeIdentifierAction: any = identifier =>
      verifyAgreementUserInternalAvatarUpload({
        identifier,
        agreementUserId: memberId
      });

    this.props.uploadFile({
      file,
      signSdk: signAgreementUserInternalAvatar,
      signData,
      finishUploadingAction,
      storeIdentifierAction
    });
  };

  exportSingleMemberCsv = () => {
    const {locationRef, member} = this.props;

    this.props.requiresPermission(
      {locationRef, permission: PERMISSIONS.EXPORT_MEMBERS_LIST},
      () =>
        this.props.exportCsv({
          exportData: {
            locationRef,
            tenantId: member.id
          },
          exportSdk: exportSingleMemberSdk,
          fileName: MEMBERS_CSV
        })
    );
  };

  render() {
    const {member, locationRef} = this.props;
    const memberAvatar = this.getMemberAvatar();
    const {avatarTabOpened} = this.state;

    return (
      <div className="MemberBox colab-medium-border-radius">
        <div className="MemberBox__tabs colab-sidepadding-2">
          {avatarTabs.map(avatarTab => (
            <div
              key={avatarTab.ref}
              className={classNames(
                'MemberBox__tabs__tab colab-clickable colab-centered colab-bold',
                {
                  'MemberBox__tabs__tab--active': this.isTabActive(
                    avatarTab.ref
                  )
                }
              )}
              onClick={() => this.changeActiveTab(avatarTab.ref)}>
              {avatarTab.label}
            </div>
          ))}
        </div>
        <MemberAvatar
          memberAvatar={memberAvatar}
          uploadAvatar={this.uploadAvatar}
          openTakePhotoModal={this.openTakePhotoModal}
          avatarTabOpened={avatarTabOpened}
        />
        <div className="MemberBox__generalInfo colab-centered colab-bold">
          {member.fullName}
        </div>
        <div className="MemberBox__infoSection colab-sidepadding-2">
          <div className="MemberBox__infoSection__row">
            <div>Balance</div>
            <div>-</div>
          </div>
          <div className="MemberBox__infoSection__row">
            <div>Current Room</div>
            <div>-</div>
          </div>
          <div className="MemberBox__infoSection__row">
            <div>Email</div>
            <div>{member.email || '-'}</div>
          </div>
          <VisibleIf
            permission={PERMISSIONS.VIEW_MEMBER_DETAILS}
            locationRef={locationRef}>
            <div className="MemberBox__infoSection__row">
              <div>Phone</div>
              <div>{member.phone || '-'}</div>
            </div>
          </VisibleIf>
        </div>
        <VisibleIf
          permission={PERMISSIONS.VIEW_MEMBER_DETAILS}
          locationRef={locationRef}>
          <div className="MemberBox__infoSection colab-sidepadding-2">
            <div className="MemberBox__infoSection__row">
              <div>Gender</div>
              <div>{member.gender || '-'}</div>
            </div>
            <div className="MemberBox__infoSection__row">
              <div>Date of birth</div>
              <div>{member.birthday || '-'}</div>
            </div>
            <div className="MemberBox__infoSection__row">
              <div>Nationality</div>
              <div>{_.get(member, 'nationality.name')}</div>
            </div>
            <div className="MemberBox__infoSection__row">
              <div>ID</div>
              <div>-</div>
            </div>
          </div>
        </VisibleIf>
        <VisibleIf
          permission={PERMISSIONS.EXPORT_MEMBERS_LIST}
          locationRef={locationRef}>
          <div className="MembersButtons">
            <Button
              white
              onClick={this.exportSingleMemberCsv}
              disabled={member.exporting}>
              Export Member Info
            </Button>
          </div>
        </VisibleIf>
      </div>
    );
  }
}

const mapStateToProps = state => {
  return {
    member: selectMember(state),
    exportingMembersCsv: selectExporting(state, MEMBERS_CSV)
  };
};

const mapDispatchToProps = {
  fetchMemberForDashboard,
  openModal,
  requiresPermission,
  uploadFile,
  exportCsv
};

export default connect(mapStateToProps, mapDispatchToProps)(MemberBox);
